import { Link } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatZAR } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const shipping = subtotal >= 2000 ? 0 : 150;

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-6 py-16">
      <p className="small-label">Your bag</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3">Cart</h1>

      {items.length === 0 ? (
        <div className="mt-16 text-center">
          <p className="text-stone">Your bag is currently empty.</p>
          <Link to="/shop" data-testid="cart-empty-shop-button" className="btn-primary mt-6">Begin shopping</Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            {items.map((item) => (
              <div key={item.key} className="flex gap-5 border-b border-stone-line pb-6">
                <Link to={`/products/${item.slug}`} className="shrink-0">
                  <img src={item.image} alt={item.title} className="w-28 h-32 object-cover rounded-2xl bg-nude" />
                </Link>
                <div className="flex-1">
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg">{item.title}</p>
                      <p className="small-label mt-1">{item.length}″ · {item.density} · {item.color}</p>
                    </div>
                    <p className="font-medium">{formatZAR(item.price * item.quantity)}</p>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="inline-flex items-center border border-stone-line rounded-full">
                      <button onClick={() => updateQty(item.key, item.quantity - 1)} className="p-2"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="px-3 text-sm">{item.quantity}</span>
                      <button onClick={() => updateQty(item.key, item.quantity + 1)} className="p-2"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <button onClick={() => removeItem(item.key)} className="text-stone hover:text-charcoal text-xs uppercase tracking-luxe inline-flex items-center gap-1">
                      <Trash2 strokeWidth={1.5} className="w-4 h-4" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <aside className="bg-nude rounded-3xl p-8 h-fit">
            <p className="small-label">Order summary</p>
            <h2 className="font-serif text-2xl mt-1">Total</h2>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-page-subtotal">{formatZAR(subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatZAR(shipping)}</span></div>
              <div className="flex justify-between text-xs text-stone">
                <span>{shipping === 0 ? "You unlocked free shipping" : `Add ${formatZAR(2000 - subtotal)} for free shipping`}</span>
              </div>
              <div className="border-t border-stone-line pt-3 flex justify-between font-medium">
                <span>Estimated total</span><span>{formatZAR(subtotal + shipping)}</span>
              </div>
            </div>
            <Link to="/checkout" data-testid="cart-checkout-link" className="btn-primary w-full mt-6">Checkout</Link>
            <p className="text-xs text-stone text-center mt-4">Secure payment by Yoco · PayFast · Ozow · Payflex</p>
          </aside>
        </div>
      )}
    </div>
  );
}
